import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@obo-account/account/account.service';
import { ConfirmAccountService } from '@obo-account/confirmAccount/confirmAccount.service';
import { RegisterService } from '@obo-account/register/register.service';
import { AuthService } from '@obo-main/services/auth/auth.service';
import { AlertService } from '@obo-main/services/common/alert/alert.service';
import { Region } from '@obo-main/services/region/region.models';
import { RegionService } from '@obo-main/services/region/region.service';
import { AppSettings } from '@obo-main/utils/appSettings.service';
import { StateService } from '@uirouter/angular';
import { finalize, mergeMap } from 'rxjs/operators';
import { ActiveModal } from '@obo-main/services/modal/activeModal.model';

@Component({
    selector: 'app-register-modal',
    templateUrl: './registerModal.component.html',
    styleUrls: ['./registerModal.component.scss'],
    standalone: false
})
export class RegisterModalComponent implements OnInit {
    public showCodeEntry: boolean;
    public passwordPattern: string;
    public regionList: Region[];
    public selectedRegion?: Region;
    private registrationInformation: {
        username: string;
        password: string;
        userId: number;
    };
    public registerBtnLoading: boolean = false;
    public confirmBtnLoading: boolean = false;
    public registrationMode: boolean;
    public registrationEnabled: boolean = true;
    public title = '';
    reCaptchaValid: boolean = false;

    public get tosAcceptText(): string {
        let text = this.translateService.instant('REGISTERFORM_TOSACCEPTED');
        let privacyReplacement = this.translateService.instant('REGISTERFORM_PRIVACY');
        let tosReplacement = this.translateService.instant('REGISTERFORM_TOS');
        let tosUrl = `<a target="_blank" href="${this.stateService.href('termsOfService', {})}">${tosReplacement}</a>`;
        let privacyUrl = `<a target="_blank" href="${this.stateService.href('privacy', {})}">${privacyReplacement}</a>`;
        return text.replace('{{TOS_URL}}', tosUrl).replace('{{PRIVACY_URL}}', privacyUrl);
    }

    constructor(
        private appSettings: AppSettings,
        private alertService: AlertService,
        private accountService: AccountService,
        private registerService: RegisterService,
        private regionService: RegionService,
        private translateService: TranslateService,
        private stateService: StateService,
        private authService: AuthService,
        private accountConfirmService: ConfirmAccountService,
        @Inject(ActiveModal) public activeModal: ActiveModal
    ) {
        this.accountService.getPasswordPattern().subscribe((pattern) => (this.passwordPattern = pattern));
    }

    public advantages = [
        'LANDING_ADVANTAGE_PROJECTS',
        'LANDING_ADVANTAGE_PLANNINGS',
        'LANDING_ADVANTAGE_EXPORTS',
        'LANDING_ADVANTAGE_DOWNLOADS'
    ];

    public ngOnInit() {
        this.regionService.getRegions().subscribe((regions) => {
            this.regionList = regions;
            this.selectedRegion = regions.find((r) => r.id === this.regionService.selectedPortalRegion.id);
        });

        this.registrationEnabled = this.appSettings.getItem('settings.enable_registration');
        this.toggleRegistrationMode(true);
    }

    public login(form: NgForm) {
        this.authService.login(form.value.username, form.value.password).subscribe({
            next: () => {
                this.activeModal.close();
            },
            error: () => {
                this.alertService.danger(this.translateService.instant('LOGINCTRL_LOGINFAILURE'));
            }
        });
    }

    public register(form: NgForm) {
        this.registerBtnLoading = true;
        this.registerService
            .register(form.value)
            .pipe(finalize(() => (this.registerBtnLoading = false)))
            .subscribe({
                next: (res) => {
                    this.registrationInformation = {
                        username: form.value.email,
                        password: form.value.password,
                        userId: res.id
                    };
                    this.showCodeEntry = true;
                },
                error: (error: any) => {
                    if (error.status === 409) {
                        this.stateService.go('alreadyRegistered', {
                            email: form.value.email
                        });
                    } else {
                        this.alertService.danger(this.translateService.instant('LOGINCTRL_REGISTERFAILURE'));
                    }
                }
            });
    }

    public toggleRegistrationMode(toggleRegistration: boolean = false): void {
        this.registrationMode = toggleRegistration && this.registrationEnabled;
        if (!this.registrationEnabled) {
            this.alertService.danger('Registration is currently disabled!');
        }
    }

    public confirmAccount(form: NgForm) {
        const code = form.value.confirmationCode;
        const userId = this.registrationInformation.userId;
        this.confirmBtnLoading = true;
        this.accountConfirmService
            .activateUserAccount(code, userId as unknown as string)
            .pipe(
                mergeMap(() =>
                    this.authService.login(this.registrationInformation.username, this.registrationInformation.password)
                ),
                finalize(() => (this.confirmBtnLoading = false))
            )
            .subscribe({
                next: () => {
                    this.activeModal.close();
                },
                error: (err) => this.alertService.danger('LOGINCTRL_ACCOUNTCONFIRMFAILURE')
            });
    }
}
